import React, { useEffect } from 'react';
import { createInstance, OptimizelyProvider, useDecision, enums } from '@optimizely/react-sdk';
import CookieConsentStore from '../stores/CookieConsentStore';
import GaDataStore from '../stores/GaDataStore';
import CookieApi from '../util/CookieApi';
import { TEST_DISABLED, OPTIMIZELY_SDK_KEY, onDecision } from '../util/ABTestDecider';

export interface ABComponentProps {
  featureKey: string;
  variationOff: React.JSX.Element | React.ReactElement;
  variations: { [key: string]: React.ReactElement | React.JSX.Element };
}

const ABTestedComponent = ({ featureKey, variationOff, variations }: ABComponentProps): React.ReactElement | React.JSX.Element => {
  const [decision] = useDecision(featureKey);
  const variationKey = decision.variationKey;
  const variation = variations[variationKey];

  return decision.enabled && variation ? variation : variationOff;
};

export default function ABTestWrapperOptimizely({ featureKey, variationOff, variations }: ABComponentProps) {
  const statisticsConsent = CookieConsentStore.isStatisticsCookiesEnabled();

  if (!statisticsConsent) {
    return variationOff;
  }

  const userId = CookieApi.getCookieValue('_ga');

  if (!userId) {
    GaDataStore.pushOptimizelyEvent(featureKey, TEST_DISABLED);
    return variationOff;
  }

  const optimizely = createInstance({
    sdkKey: OPTIMIZELY_SDK_KEY,
  });

  if (!optimizely) {
    GaDataStore.pushOptimizelyEvent(featureKey, TEST_DISABLED);
    return variationOff;
  }

  useEffect(() => {
    const notificationId = optimizely.notificationCenter.addNotificationListener(enums.NOTIFICATION_TYPES.DECISION, onDecision);
    return () => {
      optimizely.notificationCenter.removeNotificationListener(notificationId);
    };
  }, []);

  return (
    <OptimizelyProvider optimizely={optimizely} user={{ id: userId }}>
      <ABTestedComponent featureKey={featureKey} variationOff={variationOff} variations={variations} />
    </OptimizelyProvider>
  );
}
